import * as THREE from 'three';
import { U } from './constants';
import { state } from './state';
import { refs } from './refs';
import { scene, envMap } from './scene';
import { makeGradientBg } from './textures';

let sweepMesh: THREE.Mesh | null = null;
let shadowCatcher: THREE.Mesh | null = null;
let gradientBg: THREE.CanvasTexture | null = null;

function buildSweepGeometry(maxDim: number, floorY: number): THREE.BufferGeometry {
  const halfW = maxDim * 6,
    frontZ = maxDim * 5;
  const R = maxDim * 2.4,
    wallH = maxDim * 6,
    zCurve = -maxDim * 1.3;
  const prof: { z: number; y: number }[] = [];
  const flatSeg = 6;
  for (let i = 0; i < flatSeg; i++) {
    const t = i / flatSeg;
    prof.push({ z: frontZ + (zCurve - frontZ) * t, y: floorY });
  }
  const Nc = 18;
  for (let i = 0; i <= Nc; i++) {
    const th = (i / Nc) * (Math.PI / 2);
    prof.push({ z: zCurve - R * Math.sin(th), y: floorY + R - R * Math.cos(th) });
  }
  const wallSeg = 6,
    wz = zCurve - R,
    wy0 = floorY + R;
  for (let i = 1; i <= wallSeg; i++) {
    const t = i / wallSeg;
    prof.push({ z: wz, y: wy0 + wallH * t });
  }

  const cum = [0];
  for (let i = 1; i < prof.length; i++) {
    const dz = prof[i].z - prof[i - 1].z,
      dy = prof[i].y - prof[i - 1].y;
    cum.push(cum[i - 1] + Math.hypot(dz, dy));
  }
  const total = cum[cum.length - 1] || 1;
  const pos: number[] = [],
    uv: number[] = [];
  for (let i = 0; i < prof.length - 1; i++) {
    const p0 = prof[i],
      p1 = prof[i + 1],
      v0 = cum[i] / total,
      v1 = cum[i + 1] / total;
    const A = [-halfW, p0.y, p0.z],
      B = [halfW, p0.y, p0.z],
      C = [halfW, p1.y, p1.z],
      D = [-halfW, p1.y, p1.z];
    pos.push(...A, ...B, ...C, ...A, ...C, ...D);
    uv.push(0, v0, 1, v0, 1, v1, 0, v0, 1, v1, 0, v1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.computeVertexNormals();
  return g;
}

export function refreshBackdrop(): void {
  const maxDim = Math.max(state.W, state.H, state.S) * U;

  // remove previous ground
  if (sweepMesh) {
    scene.remove(sweepMesh);
    sweepMesh.geometry.dispose();
    (sweepMesh.material as THREE.Material).dispose();
    sweepMesh = null;
  }

  if (!shadowCatcher) {
    shadowCatcher = new THREE.Mesh(
      new THREE.PlaneGeometry(400, 400),
      new THREE.ShadowMaterial({ opacity: 0.3 }),
    );
    shadowCatcher.rotation.x = -Math.PI / 2;
    shadowCatcher.receiveShadow = true;
    scene.add(shadowCatcher);
  }
  shadowCatcher.position.y = state.groundY;

  const uniformFlat = state.backdropMode === 'flat' && state.bdUniform;
  const showGround =
    (state.backdropMode === 'studio' || state.backdropMode === 'flat') && !uniformFlat;

  if (showGround) {
    const geo =
      state.backdropMode === 'studio'
        ? buildSweepGeometry(maxDim, state.groundY)
        : (() => {
            const g = new THREE.PlaneGeometry(maxDim * 16, maxDim * 16);
            g.rotateX(-Math.PI / 2);
            g.translate(0, state.groundY, 0);
            return g;
          })();
    const mat = new THREE.MeshStandardMaterial({
      color: refs.backdropTex ? 0xffffff : new THREE.Color(state.backdropColor),
      map: refs.backdropTex || null,
      roughness: 0.92,
      metalness: 0.0,
      envMap: envMap,
      envMapIntensity: 0.12,
      side: THREE.FrontSide,
    });
    sweepMesh = new THREE.Mesh(geo, mat);
    sweepMesh.receiveShadow = true;
    scene.add(sweepMesh);
    shadowCatcher.visible = false;
  } else {
    shadowCatcher.visible = state.backdropMode !== 'transparent';
  }

  // Scene background
  if (state.backdropMode === 'transparent') {
    scene.background = null;
  } else if (state.backdropMode === 'gradient') {
    if (!gradientBg) gradientBg = makeGradientBg();
    scene.background = gradientBg;
  } else if (uniformFlat) {
    scene.background = new THREE.Color(state.backdropColor); // floor + wall = one uniform field + floating shadow
  } else {
    scene.background = refs.backdropTex
      ? new THREE.Color('#1a1a1d')
      : new THREE.Color(state.backdropColor);
  }
}
